/**
 * Unified DataSources API Types
 * API Version: V2
 * Endpoint: /api/v2/datasources
 *
 * Replaces:
 * - /api/v2/account-main-channel (type=1)
 * - /api/v2/account-influencers (type=2)
 * - /api/v2/account-partners (type=3)
 * - /api/v2/account-competitors (type=4)
 */

import type { Platform } from './channels';

// ============================================
// Type Mappings
// ============================================

export type DataSourceType = 'main_channel' | 'influencer' | 'partner' | 'competitor';

export const DATA_SOURCE_TYPE_CODES: Record<DataSourceType, number> = {
  main_channel: 1,
  influencer: 2,
  partner: 3,
  competitor: 4,
} as const;

export const DATA_SOURCE_TYPE_FROM_CODE: Record<number, DataSourceType> = {
  1: 'main_channel',
  2: 'influencer',
  3: 'partner',
  4: 'competitor',
} as const;

export const DATA_SOURCE_TYPE_LABELS: Record<DataSourceType, string> = {
  main_channel: 'Main Channel',
  influencer: 'Influencer',
  partner: 'Partner',
  competitor: 'Competitor',
} as const;

// Network ID mappings (backend uses numeric IDs)
// NOTE: API expects "network_id" not "social_network_id"
export const NETWORK_CODES: Record<Platform, number> = {
  instagram: 1,
  twitter: 2,
  youtube: 3,
  tiktok: 4,
} as const;

// Alias for backward compatibility
export const SOCIAL_NETWORK_CODES = NETWORK_CODES;

export const NETWORK_FROM_CODE: Record<number, Platform> = {
  1: 'instagram',
  2: 'twitter',
  3: 'youtube',
  4: 'tiktok',
} as const;

// Alias for backward compatibility
export const SOCIAL_NETWORK_FROM_CODE = NETWORK_FROM_CODE;

// ============================================
// Backend Response Types (Real API Structure)
// ============================================

export interface BackendDataSourceAuthor {
  id: number;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  followers?: number | null;
}

export interface BackendDataSourceAccount {
  id: number;
  name: string;
  description: string | null;
}

/**
 * Backend Date range for an item
 * Each item can have multiple date ranges with different statuses
 */
export interface BackendDataSourceItemDate {
  id: number;
  start_date: string; // ISO 8601
  end_date: string | null; // ISO 8601
  status: 0 | 1 | 2 | 3; // 0=new, 1=not_analyzed, 2=in_progress, 3=analyzed
}

/**
 * Backend Item = 1 social network for a datasource
 * A datasource can have multiple items (multi-platform)
 */
export interface BackendDataSourceItem {
  id: number;
  datasource_id: number;
  author_id: number;
  social_network_id: number; // 1=IG, 2=Twitter, 3=YT, 4=TikTok
  is_active: boolean;
  dates: BackendDataSourceItemDate[];
  author: BackendDataSourceAuthor;
}

/**
 * Backend DataSource = Grouped by name
 * Contains multiple items (1 per social network)
 */
export interface BackendDataSource {
  id: number;
  account_id: number;
  name: string; // Display name (e.g., "ZeroZero", "Main Competitor")
  data_source_type: number; // 1=main, 2=influencer, 3=partner, 4=competitor
  data_source_type_label: string;
  is_active: boolean;
  created_at: string; // ISO 8601
  updated_at: string; // ISO 8601
  account: BackendDataSourceAccount;
  items: BackendDataSourceItem[];
  items_count: number;
}

/**
 * Backend Paginated Response
 */
export interface BackendDataSourcesPaginatedResponse {
  data: BackendDataSource[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number | null;
    last_page: number;
    links: Array<{
      url: string | null;
      label: string;
      active: boolean;
    }>;
    path: string;
    per_page: number;
    to: number | null;
    total: number;
  };
}

// ============================================
// UI Types (Flattened for easier usage)
// ============================================

/**
 * Flattened DataSource for UI
 * Each row = 1 social network (easier to display in lists)
 */
export interface DataSource {
  id: number; // Item ID
  datasource_id: number; // Parent datasource ID
  datasource_name: string; // Display name
  account: BackendDataSourceAccount;
  author: {
    id: number;
    username: string;
    full_name: string | null;
    profile_pic_url: string | null;
    is_verified: boolean;
    followers_count: number | null;
  };
  type: DataSourceType;
  platform: Platform;
  is_active: boolean;
  analysis_since: string | null; // start_date renamed
  analysis_end: string | null; // end_date renamed
  created_at: string;
  updated_at: string;
  notes?: string | null; // For compatibility
}

// ============================================
// Request Types (for Create/Update)
// ============================================

/**
 * Create DataSource Request
 * Backend expects this structure
 */
export interface CreateDataSourceRequest {
  account_id?: number; // Optional - uses first account of user if not provided
  name: string; // Display name
  data_source_type: number; // 1,2,3,4
  is_active?: boolean; // Optional
  items?: Array<{
    author_id?: number; // Either author_id OR author_username required
    author_username?: string; // Either author_id OR author_username required
    network_id: number; // 1=IG, 2=Twitter, 3=YT, 4=TikTok
    dates?: Array<{
      start_date: string; // YYYY-MM-DD
      end_date?: string | null; // YYYY-MM-DD
      status?: 0 | 1 | 2 | 3; // 0=new, 1=not_analyzed, 2=in_progress, 3=analyzed
    }>;
  }>;
}

/**
 * Date object for updates - can be new or existing
 */
export type UpdateDateRequest =
  | {
      id: number; // Existing date - must include id
      start_date: string;
      end_date?: string | null;
      status: 0 | 1 | 2 | 3;
    }
  | {
      // New date - no id
      start_date: string;
      end_date?: string | null;
      status?: 0 | 1 | 2 | 3;
    };

/**
 * Update DataSource Request
 * Used to update datasource and its items
 * IMPORTANT: PUT /api/v2/datasources/{id} is DESTRUCTIVE
 * Items not included in the array will be DELETED
 */
export interface UpdateDataSourceRequest {
  name?: string;
  data_source_type?: number; // 1,2,3,4
  is_active?: boolean;
  items?: Array<{
    id?: number; // If ID present = update, if not = create new
    author_id?: number;
    author_username?: string;
    network_id?: number;
    dates?: UpdateDateRequest[];
  }>;
}

// ============================================
// Filters
// ============================================

export interface DataSourcesFilters {
  account_id?: number;
  type?: DataSourceType;
  platform?: Platform;
  is_active?: boolean;
  search?: string;
  per_page?: number;
  page?: number;
}

// ============================================
// Response Types (Adapted for UI)
// ============================================

export interface DataSourcesPaginatedResponse {
  data: DataSource[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

/**
 * Grouped DataSource for UI (keeps items together)
 * Used when you need to display datasources with all their platforms grouped
 */
export interface GroupedDataSource {
  id: number; // Datasource ID
  name: string; // Display name
  account: BackendDataSourceAccount;
  type: DataSourceType;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  items: Array<{
    id: number; // Item ID
    author: {
      id: number;
      username: string;
      full_name: string | null;
      profile_pic_url: string | null;
      is_verified: boolean;
      followers_count: number | null;
    };
    platform: Platform;
    is_active: boolean;
    analysis_since: string | null;
    analysis_end: string | null;
  }>;
}

/**
 * Grouped Paginated Response
 */
export interface GroupedDataSourcesPaginatedResponse {
  data: GroupedDataSource[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// ============================================
// Adapter Functions
// ============================================

/**
 * Convert backend DataSource to flat UI DataSources
 * 1 Backend DataSource → N UI DataSources (1 per item/platform)
 */
export function adaptBackendDataSource(backend: BackendDataSource): DataSource[] {
  const type = DATA_SOURCE_TYPE_FROM_CODE[backend.data_source_type] || 'competitor';

  return backend.items.map((item) => {
    // Use the first date range (dates[0]) for UI display
    const firstDate = item.dates?.[0];

    return {
      id: item.id,
      datasource_id: backend.id,
      datasource_name: backend.name,
      account: backend.account,
      author: {
        id: item.author.id,
        username: item.author.username,
        full_name: item.author.full_name,
        profile_pic_url: item.author.avatar_url,
        is_verified: false, // Backend doesn't provide this
        followers_count: null, // Backend doesn't provide this
      },
      type,
      platform: SOCIAL_NETWORK_FROM_CODE[item.social_network_id] || 'instagram',
      is_active: item.is_active,
      analysis_since: firstDate?.start_date || null,
      analysis_end: firstDate?.end_date || null,
      created_at: backend.created_at,
      updated_at: backend.updated_at,
      notes: null,
    };
  });
}

/**
 * Convert backend paginated response to UI format
 */
export function adaptBackendPaginatedResponse(
  backend: BackendDataSourcesPaginatedResponse
): DataSourcesPaginatedResponse {
  // Safety check: ensure backend.data exists
  if (!backend || !backend.data) {
    return {
      data: [],
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
      },
    };
  }

  const flattenedData: DataSource[] = [];

  backend.data.forEach((backendDS) => {
    const adaptedSources = adaptBackendDataSource(backendDS);
    flattenedData.push(...adaptedSources);
  });

  return {
    data: flattenedData,
    meta: {
      current_page: backend.meta.current_page,
      last_page: backend.meta.last_page,
      per_page: backend.meta.per_page,
      total: backend.meta.total,
    },
  };
}

/**
 * Convert backend DataSource to grouped UI DataSource
 * Keeps items grouped by datasource (1 datasource = 1 GroupedDataSource)
 */
export function adaptBackendDataSourceGrouped(backend: BackendDataSource): GroupedDataSource {
  const type = DATA_SOURCE_TYPE_FROM_CODE[backend.data_source_type] || 'competitor';

  const adapted = {
    id: backend.id,
    name: backend.name,
    account: backend.account,
    type,
    is_active: backend.is_active,
    created_at: backend.created_at,
    updated_at: backend.updated_at,
    items: (backend.items || []).map((item) => {
      // Use the first date range (dates[0]) for UI display
      const firstDate = item.dates?.[0];

      return {
        id: item.id,
        author: {
          id: item.author.id,
          username: item.author.username,
          full_name: item.author.full_name,
          profile_pic_url: item.author.avatar_url,
          is_verified: false,
          followers_count: item.author.followers || null,
        },
        platform: SOCIAL_NETWORK_FROM_CODE[item.social_network_id] || 'instagram',
        is_active: item.is_active,
        analysis_since: firstDate?.start_date || null,
        analysis_end: firstDate?.end_date || null,
      };
    }),
  };

  return adapted;
}

/**
 * Convert backend paginated response to grouped UI format
 */
export function adaptBackendPaginatedResponseGrouped(
  backend: BackendDataSourcesPaginatedResponse
): GroupedDataSourcesPaginatedResponse {
  // Safety check: ensure backend.data exists
  if (!backend || !backend.data) {
    return {
      data: [],
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
      },
    };
  }

  // Map each datasource
  const mappedDatasources = backend.data.map((ds) => {
    const adapted = adaptBackendDataSourceGrouped(ds);
    return adapted;
  });

  // Filter out datasources without items
  const validDatasources = mappedDatasources.filter(ds => {
    return ds.items && ds.items.length > 0;
  });

  return {
    data: validDatasources,
    meta: {
      current_page: backend.meta.current_page,
      last_page: backend.meta.last_page,
      per_page: backend.meta.per_page,
      total: backend.meta.total,
    },
  };
}

/**
 * Helper: Convert UI filter type to backend type code
 */
export function getTypeCodeFromFilter(type?: DataSourceType): number | undefined {
  return type ? DATA_SOURCE_TYPE_CODES[type] : undefined;
}

/**
 * Helper: Convert UI platform to backend social_network_id
 */
export function getSocialNetworkIdFromPlatform(platform: Platform): number {
  return SOCIAL_NETWORK_CODES[platform];
}
